// Libs
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputLabel,
  Stack,
  Tooltip,
  Typography,
  Zoom,
} from '@mui/material';
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
// Components, Layouts, Pages
import {
  BaseFilters,
  BaseInputSearch,
  BaseSelect,
  BaseStatus,
  BaseTable,
  BaseTableOption,
  BaseTableUser,
  BaseToolbar,
  CommonDatePicker,
  ConfirmModal,
  Loading,
} from '~/components/common';
import { ChangeStatusJobModalComponent } from '~/components/module/job';
import PageShell from '~/pages/common/pageShell/PageShell';
// Others
import { adminRouteAbsolute } from '~/utils/routes/admin';
import useDebounce from '~/utils/hooks/useDebounce';
import { IJob, IQueryGetListJob } from '~/utils/interface/job';
import { IBaseOption, IFilterValueChange, IHeaderCellType } from '~/utils/interface/common';
import { IPaginationResponse } from '~/utils/interface/base';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ZERO,
  DEFAULT_VALUE_FILTER,
  EMPTY_STRING,
  FILTER_DEFAULT_VALUES,
  jobFilterPaymentOptions,
  SEARCH_BY_KEY,
} from '~/utils/constants/common';
import {
  StatusJobEnum,
  RoleEnum,
  SearchByFilterContentEnum,
  SearchByFilterEnum,
  DateFormatEnum,
  JobFilterPaymentEnum,
} from '~/utils/enum/common';
import { formatCurrency, formatDate, formatTitleCase } from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { jobActions, selectIsRefreshJobList } from '~/thunks/job/jobSlice';
import { deleteJob, getJobList } from '~/thunks/job/jobThunk';
import { StorageEnum } from '~/utils/enum/base';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { optionFilterJobType, optionFilterStatusJob } from '~/utils/mockData/job';
// Styles, images, icons
import styles from './Job.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columns = (
  t: TFunction,
  onView: (id: string) => void,
  onEdit: (id: string) => void,
  onChangeStatus: (job: IJob) => void,
  onDelete: (id: string) => void
): IHeaderCellType<IJob>[] => {
  return [
    {
      id: 'name',
      label: t('table_head_title_name'),
      render: (record: IJob) => {
        return (
          <Box component={'div'} className={cx('jobNameContainer')}>
            <Typography variant='body1' className={cx('jobNameVal')}>
              {record?.name || EMPTY_STRING}
            </Typography>

            {record?.isMissingPayment && (
              <Tooltip
                title={t('table_tooltip_job_missing_payment')}
                arrow
                placement='top'
                slots={{ transition: Zoom }}
              >
                <img
                  src={icons.commonIconWarning}
                  alt={t('common_alt_img', { ns: 'common/base' })}
                  className={cx('iconWarning')}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      id: 'client',
      label: t('table_head_title_customer'),
      render: (record: IJob) => {
        return record?.client ? (
          <BaseTableUser
            user={{
              avatar: record?.client?.avatarImage,
              firstName: record?.client?.firstName || EMPTY_STRING,
              lastName: record?.client?.lastName || EMPTY_STRING,
            }}
          />
        ) : (
          EMPTY_STRING
        );
      },
    },
    {
      id: 'address',
      label: t('table_head_title_address'),
      render: (record: IJob) => {
        return record?.address || EMPTY_STRING;
      },
    },
    {
      id: 'type',
      label: t('table_head_title_jobType'),
      render: (record: IJob) => {
        return record?.type ? formatTitleCase(record?.type) : EMPTY_STRING;
      },
    },
    {
      id: 'total',
      label: t('table_head_title_amount'),
      render: (record: IJob) => {
        return formatCurrency(Number(record?.total)) || EMPTY_STRING;
      },
      minWidth: 100,
    },
    {
      id: 'profit',
      label: t('table_head_title_profit'),
      render: (record: IJob) => {
        return formatCurrency(record?.profit) || EMPTY_STRING;
      },
      minWidth: 100,
    },
    {
      id: 'tags',
      label: t('table_head_title_job_tags'),
      render: (record: IJob) => {
        if (record?.tags && record?.tags?.length > DEFAULT_NUMBER_ZERO) {
          const listTag = record?.tags?.map((item) => item?.name);

          return listTag?.join(', ');
        }

        return EMPTY_STRING;
      },
    },
    {
      id: 'source',
      label: t('table_head_title_lead_source'),
      render: (record: IJob) => {
        return record?.leadSource ? formatTitleCase(record?.leadSource?.name) : EMPTY_STRING;
      },
    },
    {
      id: 'status',
      label: t('table_head_title_status'),
      render: (record: IJob) => (record?.status ? <BaseStatus label={record?.status} /> : EMPTY_STRING),
    },
    {
      id: 'completedAt',
      label: t('table_head_title_complete_date'),
      render: (record: IJob) =>
        record?.completedAt ? formatDate(record?.completedAt, DateFormatEnum.DD_MMM_YYYY) : EMPTY_STRING,
    },
    {
      id: 'action',
      label: t('table_head_title_action'),
      align: 'left',
      render: (record: IJob) => {
        return (
          <BaseTableOption
            onView={() => onView(record.id)}
            onEdit={() => onEdit(record.id)}
            onChangeStatus={() => onChangeStatus(record)}
            onDelete={() => onDelete(record.id)}
          />
        );
      },
    },
  ];
};

const Job = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/job', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(
    () => Number(params?.page ?? BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
    [params?.page]
  );
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  const textSearchParams = useMemo<string>(
    () => String(params?.textSearch || EMPTY_STRING),
    [params?.textSearch]
  );
  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  //#endregion Declare Hook

  //#region Selector
  const isRefreshJobList = useSelector(selectIsRefreshJobList);
  //#endregion Selector

  //#region Declare State
  const [filterParams, setFilterParams] = useState<IQueryGetListJob>(FILTER_DEFAULT_VALUES);
  const [paramObject, setParamObject] = useState<IQueryGetListJob>({
    page: pageSelected,
    limit: limitPage,
  });
  const [searchPlaceholder, setSearchPlaceholder] = useState<string>(EMPTY_STRING);
  const [jobList, setJobList] = useState<IJob[]>();
  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // const [idsJob, setIdsJob] = useState<string[]>([]);
  const [totalItems, setTotalItems] = useState<string>(EMPTY_STRING);
  const [totalMissingPayment, setTotalMissingPayment] = useState<number>();
  const [jobSelectedIds, setJobSelectedIds] = useState<string[]>([]);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);

  const [isShowChangeStatusJob, setIsShowChangeStatusJob] = useState<boolean>(false);
  const [jobSelected, setJobSelected] = useState<IJob | null>(null);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  const handleGetJobList = useCallback((params: IQueryGetListJob) => {
    setIsLoading(true);

    dispatch(getJobList(params))
      .unwrap()
      .then((resp) => {
        const { responses, pagination, totalItems, totalJobMissingPayment } = resp.data;
        if (!responses) return;

        setJobList(responses);
        setPagination(pagination);
        setTotalItems(totalItems.toString());
        setTotalMissingPayment(totalJobMissingPayment);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch]);

  useEffect(() => {
    const { status, jobType, paymentStatus, ...rest } = paramObject;

    const params = {
      ...rest,
      ...(status !== StatusJobEnum.ALL && { status }),
      ...(jobType !== DEFAULT_VALUE_FILTER && { jobType }),
      ...(paymentStatus !== JobFilterPaymentEnum.ALL && { paymentStatus }),
    };

    handleGetJobList(params);

  }, [paramObject, handleGetJobList]);

  useEffect(() => {
    if (!isRefreshJobList) return;
    const { status, jobType, paymentStatus, ...rest } = paramObject;

    const params = {
      ...rest,
      ...(status !== StatusJobEnum.ALL && { status }),
      ...(jobType !== DEFAULT_VALUE_FILTER && { jobType }),
      ...(paymentStatus !== JobFilterPaymentEnum.ALL && { paymentStatus }),
    };

    handleGetJobList(params);
    dispatch(jobActions.setRefreshJobList(false));
  }, [isRefreshJobList, handleGetJobList, paramObject, dispatch]);

  useEffect(() => {
    setSearchKey(textSearchParams);
  }, [textSearchParams]);

  useEffect(() => {
    if (Array(filterParams.fields)) {
      const placeholder = filterParams.fields?.map((item) => formatTitleCase(item))?.join(', ');
      setSearchPlaceholder(placeholder as string);
    }
  }, [filterParams.fields]);

  useEffect(() => {
    setParamObject(prev => ({
      ...prev,
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: limitPage,
      ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      ...(filterParams ? { ...filterParams } : {}),
    }));

    setSearchParams({
      page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
      limit: String(limitPage),
      ...(searchKey ? { textSearch: debounceSearchKey } : {}),
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey, limitPage, filterParams, searchKey]);

  useEffect(() => {
    if (!searchKey) return;

    setParamObject(prev => ({
      ...prev,
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: limitPage,
      ...(filterParams ? { ...filterParams } : {}),
    }));
  }, [filterParams, searchKey, limitPage]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleNavigateToFormJob = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.createJob);
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.createJob);
        break;
    }
  };

  const handleApplyFilter = (values: IQueryGetListJob | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    if (values?.status || values?.jobType || values?.startTime || values?.endTime || values?.paymentStatus) {
      setParamObject({
        ...paramObject,
        ...values,
      });
    }
  };

  const onFilterChildrenChange = (
    event: ChangeEvent<HTMLInputElement>,
    valueFilter: IQueryGetListJob | undefined,
    onChange?: (value: IFilterValueChange<IQueryGetListJob>) => void
  ) => {
    const { name, value, checked } = event.target;
    const newFields: IQueryGetListJob = {
      ...filterParams,
      fields: checked
        ? [...(valueFilter?.fields || []), value]
        : (valueFilter?.fields || [])?.filter((item) => item !== value),
    };

    if (name === SEARCH_BY_KEY && onChange) {
      onChange({ name: 'fields', value: newFields?.fields });
    }
  };

  const handleRowTableClick = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.jobs}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.jobs}/${id}`);
        break;
    }
  };

  const handleViewJob = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.jobs}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.jobs}/${id}`);
        break;
    }
  };

  const handleEditJob = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.updateJob(id)}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.updateJob(id)}`);
        break;
    }
  };

  const handleChangeStatusJob = (job: IJob) => {
    if (!job) return;

    setJobSelected(job);
    setIsShowChangeStatusJob(true);
  };

  const handleDeleteJob = (id: string) => {
    if (!id) return;

    setJobSelectedIds([id]);
    setIsShowConfirmDelete(true);
  };

  const handleConfirmDeleteJob = () => {
    if (!jobSelectedIds || jobSelectedIds?.length === DEFAULT_NUMBER_ZERO) return;

    setIsLoading(true);

    dispatch(deleteJob({ deleteIds: jobSelectedIds }))
      .unwrap()
      .then((res) => {
        dispatch(jobActions.setRefreshJobList(true));
        setJobSelectedIds([]);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
        setIsShowConfirmDelete(false);
      });
  };

  const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  // const handleRowsSelect = (ids: string[]) => {
  //   // TODO: Handle logic row select
  //   setIdsJob(ids);
  // };

  const handleRowsSelect = (ids: string[]) => {
    setJobSelectedIds(ids);
  };

  const handleRowPerPageChange = (rowPerPage: number) => {
    setParamObject({ ...paramObject, page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: rowPerPage });
    setSearchParams({
      ...params,
      page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
      limit: String(rowPerPage),
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleDeleteMultiple = () => {
    if (!jobSelectedIds || jobSelectedIds.length === DEFAULT_NUMBER_ZERO) return;
    setIsShowConfirmDelete(true);
  };

  const handleCloseConfirmDelete = () => {
    setIsShowConfirmDelete(false);
    setJobSelectedIds([]);
  };

  const handleCloseChangeStatusJob = () => {
    setIsShowChangeStatusJob(false);
    setJobSelected(null);
  };

  const handleFilterChange = (
    name: keyof IQueryGetListJob,
    option: IBaseOption,
    onChange?: (value: IFilterValueChange<IQueryGetListJob>) => void
  ) => {
    if (!option) return;

    const { value } = option;

    onChange?.({ name: name, value: value });
  };
  //#endregion Handle Function

  return (
    <PageShell title={t('menu_jobs', { ns: 'common/sidebar' })}>
    <Box component={'div'} id='jobPageAdmin' className={cx('container')}>
      <BaseToolbar
        title={t('tile_quantity_job_page', { quantity: totalItems })}
        secondaryTitle={t('tile_quantity_job_missing_payment_page', {
          quantity: totalMissingPayment ?? DEFAULT_NUMBER_ZERO,
        })}
        textAction={t('button_add_job')}
        onAction={handleNavigateToFormJob}
        onDelete={handleDeleteMultiple}
        selectedCount={jobSelectedIds.length}
      >
        <BaseInputSearch
          searchByContent={
            searchPlaceholder
              ? `${SearchByFilterContentEnum.NAME}, ${searchPlaceholder}`
              : SearchByFilterContentEnum.NAME
          }
          value={searchKey}
          onChange={handleSearchChange}
        />

        <BaseFilters<IQueryGetListJob>
          value={filterParams}
          defaultValue={FILTER_DEFAULT_VALUES}
          onApply={handleApplyFilter}
        >
          {({ value, onChange }) => (
            <Stack gap={'12px'}>
              <Typography className={cx('title')}>
                {t('common_action_search_by', { ns: 'common/base' })}
              </Typography>

              <FormGroup>
                <FormControlLabel
                  className={cx('formControlLabel')}
                  slotProps={{
                    typography: {
                      className: cx('checkboxLabel'),
                    },
                  }}
                  control={
                    <Checkbox
                      name={SEARCH_BY_KEY}
                      className={cx('checkbox')}
                      value={SearchByFilterEnum.CUSTOMER}
                      checked={value?.fields?.includes(SearchByFilterEnum.CUSTOMER) || false}
                      onChange={(event) => onFilterChildrenChange(event, value, onChange)}
                    />
                  }
                  label={t('filter_label_customer')}
                />

                <FormControlLabel
                  className={cx('formControlLabel')}
                  slotProps={{
                    typography: {
                      className: cx('checkboxLabel'),
                    },
                  }}
                  control={
                    <Checkbox
                      name={SEARCH_BY_KEY}
                      className={cx('checkbox')}
                      value={SearchByFilterEnum.JOB_TAG}
                      checked={value?.fields?.includes(SearchByFilterEnum.JOB_TAG) || false}
                      onChange={(event) => onFilterChildrenChange(event, value, onChange)}
                    />
                  }
                  label={t('filter_label_job_tag')}
                />

                <FormControlLabel
                  className={cx('formControlLabel')}
                  slotProps={{
                    typography: {
                      className: cx('checkboxLabel'),
                    },
                  }}
                  control={
                    <Checkbox
                      name={SEARCH_BY_KEY}
                      className={cx('checkbox')}
                      value={SearchByFilterEnum.LEAD_SOURCE}
                      checked={value?.fields?.includes(SearchByFilterEnum.LEAD_SOURCE) || false}
                      onChange={(event) => onFilterChildrenChange(event, value, onChange)}
                    />
                  }
                  label={t('filter_label_lead_source')}
                />
              </FormGroup>

              <Typography className={cx('title')}>
                {t('common_action_filter_by', { ns: 'common/base' })}
              </Typography>

              <Box component={'div'} className={cx('filterGroup')}>
                <InputLabel className={cx('label')}>{t('filter_label_lead_status')}</InputLabel>

                <BaseSelect
                  layout='horizontal'
                  width={'240px'}
                  options={optionFilterStatusJob}
                  value={value?.status || StatusJobEnum.ALL}
                  onChange={(option) => handleFilterChange('status', option, onChange)}
                />
              </Box>

              <Box component={'div'} className={cx('filterGroup')}>
                <InputLabel className={cx('label')}>{t('filter_label_lead_job_type')}</InputLabel>

                <BaseSelect
                  layout='horizontal'
                  width={'240px'}
                  options={optionFilterJobType}
                  value={value?.jobType || DEFAULT_VALUE_FILTER}
                  onChange={(option) => handleFilterChange('jobType', option, onChange)}
                />
              </Box>

              <Box component={'div'} className={cx('filterGroup')}>
                <InputLabel className={cx('label')}>{t('filter_label_job_payment')}</InputLabel>

                <BaseSelect
                  layout='horizontal'
                  width={'240px'}
                  options={jobFilterPaymentOptions}
                  value={value?.paymentStatus || JobFilterPaymentEnum.ALL}
                  onChange={(option) => handleFilterChange('paymentStatus', option, onChange)}
                />
              </Box>

              <Box component={'div'} className={cx('filterGroup')}>
                <InputLabel className={cx('label')}>{t('filter_label_job_completed_date')}</InputLabel>

                <CommonDatePicker
                  width={'240px'}
                  mode='rangeDate'
                  value={{
                    startDate: value?.startTime || EMPTY_STRING,
                    endDate: value?.endTime || EMPTY_STRING,
                  }}
                  onChange={({ startDate, endDate }) => {
                    onChange && onChange({ name: 'startTime', value: startDate });
                    onChange && onChange({ name: 'endTime', value: endDate });
                  }}
                />
              </Box>
            </Stack>
          )}
        </BaseFilters>
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          currentRowsPerPage={limitPage}
          currentPage={pageSelected}
          count={pagination?.totalPages || DEFAULT_NUMBER_ZERO}
          columns={columns(t, handleViewJob, handleEditJob, handleChangeStatusJob, handleDeleteJob)}
          dataSource={jobList || []}
          dataSelectedIds={jobSelectedIds}
          onPageChange={handlePageChange}
          onSelectChange={handleRowsSelect}
          onRowClick={handleRowTableClick}
          onRowsPerPageChange={handleRowPerPageChange}
        />
      </Box>

      {isLoading && <Loading />}

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDeleteJob}
        />
      )}

      {isShowChangeStatusJob && (
        <ChangeStatusJobModalComponent
          onClose={handleCloseChangeStatusJob}
          {...(jobSelected && { data: jobSelected })}
        />
      )}
    </Box>
    </PageShell>
  );
};

export default Job;
